import { useMemo, useState } from 'react';
import { Phone } from 'lucide-react';

import { CustomSelect } from './custom-select.js';
import { PHONE_CODES } from '../constants/phone-codes.js';
import { useTranslation } from '../use-translation.js';

export interface PhoneFieldProps {
  readonly id?: string;
  readonly label?: string;
  readonly countryCode: string;
  readonly value: string;
  readonly onCountryChange: (countryCode: string) => void;
  readonly onChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly disabled?: boolean;
  readonly placeholder?: string;
}

export function PhoneField({
  id,
  label,
  countryCode,
  value,
  onCountryChange,
  onChange,
  error,
  disabled = false,
  placeholder,
}: PhoneFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const effectiveLabel = label ?? t('auth.mobile');
  const effectivePlaceholder = placeholder ?? t('auth.mobilePlaceholder');
  const [selectedValue, setSelectedValue] = useState('');

  const selected = useMemo(() => {
    const fromKey = PHONE_CODES.find(
      (c) => c.value === selectedValue && c.value.startsWith(`${countryCode}-`),
    )?.value;
    return fromKey ?? PHONE_CODES.find((c) => c.value.startsWith(`${countryCode}-`))?.value ?? '';
  }, [countryCode, selectedValue]);

  const handleCountryChange = (next: string): void => {
    setSelectedValue(next);
    const dialingCode = next.split('-')[0] ?? '';
    onCountryChange(dialingCode);
  };

  const safeValue = value.replace(/[^\d\s\-+]/g, '');

  return (
    <div>
      <label htmlFor={id} className="ibe-label">
        {effectiveLabel}
      </label>
      <div className="flex gap-2">
        <CustomSelect
          id="phone-country-code"
          value={selected}
          onChange={handleCountryChange}
          options={PHONE_CODES}
          placeholder={t('common.code')}
          searchable
          searchPlaceholder={t('common.searchCode')}
          disabled={disabled}
          ariaLabel={t('auth.dialingCode')}
          className="w-auto min-w-[6.5rem] shrink-0"
          triggerClassName="ibe-control h-full w-auto min-w-[6.5rem] shrink-0"
        />
        <div className="relative w-full">
          <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id={id}
            type="tel"
            value={safeValue}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder={effectivePlaceholder}
            className="ibe-control w-full pl-10"
            aria-invalid={error !== undefined && error !== ''}
            disabled={disabled}
          />
        </div>
      </div>
      {error !== undefined && error !== '' && <p className="ibe-error">{error}</p>}
    </div>
  );
}
